<script lang="ts">
import { defineComponent } from "vue";
import KlereoBanner from "@/shared/components/KlereoBanner.vue";
import { notesServices } from "@/features/notes/api/notes.service";
import { Note } from "@/features/notes/api/notes.models";
import NoteForm from "@/features/notes/NoteForm.vue";
import { Nullable } from "@/models/nullable.type";
import KlereoIcon from "@/shared/components/KlereoIcon.vue";
import { injectToken } from "@/shared/utils/injection";
import KlereoModal from "@/shared/components/KlereoModal.vue";
import { subSeconds } from "date-fns";
import PromiseWrapper from "@/shared/components/PromiseWrapper.vue";

export default defineComponent({
  ...injectToken,
  components: {
    PromiseWrapper,
    KlereoModal,
    KlereoIcon,
    NoteForm,
    KlereoBanner,
  },
  emits: ["openSidebar"],
  data() {
    return {
      adding: false,
      editing: null as Nullable<Note>,
      removing: null as Nullable<Note>,
      notes: [] as Note[],
      promise: null as Nullable<Promise<void>>,
      refreshing: false,
      loading: false,
      searchQuery: "",
    };
  },
  created() {
    this.refresh();
  },
  methods: {
    refresh() {
      this.loading = true;
      this.promise = notesServices.getNotes().then((notes) => {
        this.notes = notes;
        this.loading = false;
      });
    },
    remove(noteId: number) {
      notesServices.deleteNote({ msgID: noteId }).then(() => {
        this.removing = null;
        this.refresh();
      });
    },
    formatDate(note: Note) {
      return subSeconds(new Date(), note.updateTime).toLocaleString(undefined, {
        dateStyle: "short",
        timeStyle: "short",
      });
    },
  },
  computed: {
    filteredNotes() {
      const query = this.searchQuery.toLowerCase();
      return this.notes.filter(
        (note) =>
          note.message.toLowerCase().includes(query) ||
          note.poolNickname.toLowerCase().includes(query) ||
          note.userNickname.toLowerCase().includes(query)
      );
    },
  },
});
</script>

<template>
  <NoteForm
    v-if="adding"
    @back="
      () => {
        adding = false;
        refresh ? refresh() : null;
      }
    "
  />
  <NoteForm
    v-else-if="editing"
    :note="editing"
    @back="
      () => {
        editing = null;
        refresh ? refresh() : null;
      }
    "
  />
  <div v-else>
    <klereo-banner
      @left="$emit('openSidebar')"
      left-icon="klereo"
      left-class="lg:hidden"
      @right="adding = true"
      right-icon="plus-square"
      :title="$t('Messages')"
      ><div class="hidden lg:flex items-center">
        <router-link :to="lastPool ? `/pool/${lastPool.idSystem}` : '/'"
          ><klereo-icon name="arrow-left" class="w-[27px]"
        /></router-link></div
    ></klereo-banner>

    <div class="lg:max-w-3xl lg:m-auto">
      <div class="flex mb-6 mt-10 w-1/2 m-auto">
        <klereo-icon name="search" class="w-[27px]"></klereo-icon>
        <input
          class="flex-grow mx-3 form-input"
          v-model="searchQuery"
          :placeholder="$t('Search')"
        />
      </div>

      <promise-wrapper :promise="promise" class="text-gray-200">
        <div
          class="card p-3 mb-2"
          v-for="note in filteredNotes"
          :key="note.idMessage"
        >
          <div class="flex">
            <h3 class="text-lg font-medium flex-grow">
              {{ note.poolNickname }}
            </h3>

            <div class="flex items-center" v-if="note.idUser === token.sub">
              <KlereoIcon
                name="edit"
                class="cursor-pointer mr-2"
                @click="editing = note"
              />
              <KlereoIcon
                name="trash-2"
                class="cursor-pointer"
                @click="removing = note"
              />
            </div>

            <div class="flex items-center text-sm text-gray-300" v-else>
              {{ note.userNickname }}
            </div>
          </div>

          <div class="flex justify-between">
            <p class="text-xs font-regular text-gray-300">{{ note.message }}</p>
            <div class="text-xs text-gray-300">{{ formatDate(note) }}</div>
          </div>
        </div>

        <div
          v-if="filteredNotes.length === 0"
          class="text-center text-white py-10"
        >
          {{ $t("NoNoteMatchingSearch") }}
        </div>
      </promise-wrapper>
    </div>

    <klereo-modal :opened="!!removing" @close="removing = null">
      <div class="mb-5">Supprimer cette note ?</div>

      <div class="flex gap-2 justify-end">
        <div class="btn--secondary" @click="removing = null">Non</div>
        <div class="btn--primary" @click="remove(removing!.idMessage)">Oui</div>
      </div>
    </klereo-modal>
  </div>
</template>
